import type { EmmiApp, EmmiLocale, EmmiPlatform } from "./schemas";
import { sanitize } from "./sanitize";
import { getAnonymousId, getSessionId, randomUuidV7 } from "./session";
import { getTelemetryToken, type TelemetryTokenConfig } from "./token";

/**
 * usage-event-pipeline's client (design.md: "client (SDK: sanitize -> queue,
 * batch<=50, offline store, retry)"). Vendored minimal batching sender:
 * `track()` sanitizes and enqueues; `flush()` sends every queued event as
 * its own `POST /v1/events` (services/event-ingest accepts one event per
 * request — task 4.5's own scope, see emmi-observability apply-progress
 * Batch 4A Deviation 5). Never blocks the caller: every public method
 * returns immediately or resolves without throwing.
 */
export interface TelemetryConfig extends TelemetryTokenConfig {
  /** event-ingest base URL. Empty disables the client entirely — no network calls at all. */
  ingestUrl: string;
  appKey: EmmiApp;
  platform: EmmiPlatform;
  appVersion: string;
  locale?: EmmiLocale;
}

const MAX_QUEUE_SIZE = 50;
// Hard cap independent of MAX_QUEUE_SIZE's auto-flush trigger: if flush()
// falls behind (offline, a slow/hanging endpoint, a burst of track() calls)
// the queue must not grow without bound. Beyond this, the OLDEST queued
// events are dropped — losing recent events would defeat the point of a
// usage pipeline reporting on what a user is doing right now.
const HARD_QUEUE_CAP = 200;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1_000;
// Never let a hanging event-ingest request pile up forever behind the flush
// timer; token.ts applies its own shorter timeout to the token endpoint.
const EVENT_FETCH_TIMEOUT_MS = 10_000;

interface QueuedEvent {
  envelope: Record<string, unknown>;
  attempts: number;
}

export interface IdentifyResult {
  pseudonym: string;
  keyVersion: number;
}

/**
 * Never lets a hanging fetch (a stalled connection, a backend that accepted
 * the TCP connection but never responds) keep the flush timer's queue
 * blocked indefinitely — `flushing` is always reset in `flush()`'s
 * `finally`, but that only helps once the underlying fetch itself settles.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

export class TelemetryClient {
  private readonly config: TelemetryConfig;
  private queue: QueuedEvent[] = [];
  private flushing = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private identified = false;
  private droppedCount = 0;
  /** Set once identify() confirms a pseudonym. Kept in memory only — the
   * PostHog `distinct_id` wiring itself waits for Phase 2 (see identify()'s
   * own docblock). */
  private pseudonym: string | null = null;

  constructor(config: TelemetryConfig) {
    this.config = config;
  }

  get enabled(): boolean {
    return this.config.ingestUrl.length > 0;
  }

  /** Events dropped because the hard queue cap was exceeded (batch 4B item 3). */
  get droppedEventCount(): number {
    return this.droppedCount;
  }

  /** The pseudonym identify() last confirmed, or null before the first success. */
  get currentPseudonym(): string | null {
    return this.pseudonym;
  }

  /** Whether identify() has already confirmed a pseudonym this session. */
  get isIdentified(): boolean {
    return this.identified;
  }

  /**
   * usage-event-pipeline batch 4B final correction: called by
   * resetTelemetryIdentity() on every logout. Drops every queued-but-unsent
   * event of the user who is logging out instead of sending them under the
   * next identity on a shared device, and resets identify() state so the
   * next person's first flush re-identifies instead of silently reusing the
   * previous user's pseudonym. The token cache and session id are reset
   * separately (token.ts / session.ts) — this only owns in-memory client
   * state.
   */
  resetIdentity(): void {
    this.queue = [];
    this.identified = false;
    this.pseudonym = null;
  }

  /** Starts the periodic flush timer. No-op when disabled or already started. */
  start(): void {
    if (!this.enabled || this.timer) return;
    this.timer = setInterval(() => {
      void this.flush();
      void this.maybeIdentify();
    }, FLUSH_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Sanitizes and enqueues one event. Silently no-ops when disabled or when
   * sanitize() drops the event entirely (unregistered name or a missing
   * required field) — telemetry must never throw into the caller.
   */
  track(
    eventName: string,
    properties: Record<string, unknown> = {},
    context?: Record<string, unknown>,
  ): void {
    if (!this.enabled) return;

    const mergedContext = this.config.locale
      ? { locale: this.config.locale, ...context }
      : context;
    const sanitized = sanitize({ eventName, properties, context: mergedContext });
    if (!sanitized) return;

    const now = new Date().toISOString();
    const envelope: Record<string, unknown> = {
      schema_version: "1",
      event_id: randomUuidV7(),
      event_name: sanitized.eventName,
      event_version: sanitized.eventVersion,
      occurred_at: now,
      // Re-stamped in send() right before every attempt — this initial value
      // only matters if the event is somehow inspected before its first
      // send (batch 4B item 4).
      sent_at: now,
      app: this.config.appKey,
      platform: this.config.platform,
      app_version: this.config.appVersion,
      session_id: getSessionId(),
      anonymous_id: getAnonymousId(),
      properties: sanitized.properties,
    };
    if (sanitized.context && Object.keys(sanitized.context).length > 0) {
      envelope.context = sanitized.context;
    }

    this.queue.push({ envelope, attempts: 0 });
    if (this.queue.length > HARD_QUEUE_CAP) {
      // flush() has fallen behind badly enough that auto-flushing alone
      // never caught up — drop the OLDEST events rather than let the queue
      // (and its memory) grow without bound.
      const overflow = this.queue.length - HARD_QUEUE_CAP;
      this.queue.splice(0, overflow);
      this.droppedCount += overflow;
    }
    if (this.queue.length >= MAX_QUEUE_SIZE) {
      void this.flush();
    }
  }

  /** Sends every currently queued event. Safe to call repeatedly / concurrently. */
  async flush(): Promise<void> {
    if (!this.enabled || this.flushing || this.queue.length === 0) return;
    this.flushing = true;
    const batch = this.queue;
    this.queue = [];
    try {
      const authHeader = await this.resolveAuthHeader();
      await Promise.all(batch.map((item) => this.send(item, authHeader)));
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Calls `POST /v1/identity` once per session so the app can set its
   * pseudonym as the PostHog `distinct_id` / Crashlytics user id (D-7).
   * Retried lazily from the flush timer (`maybeIdentify`) so a login that
   * happens after the client already started still gets picked up without
   * a page reload. `identified` is only ever set true in the same branch
   * that already confirmed and stored a real `pseudonym` — never before.
   * The PostHog/Crashlytics wiring itself is Phase 2 scope: this stores the
   * pseudonym in memory for that later consumer, nothing reads it yet.
   */
  async identify(): Promise<IdentifyResult | null> {
    if (!this.enabled) return null;
    const token = await getTelemetryToken(this.config);
    if (!token) return null;

    try {
      const response = await fetchWithTimeout(
        `${this.config.ingestUrl}/v1/identity`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
        EVENT_FETCH_TIMEOUT_MS,
      );
      if (!response.ok) return null;
      const body = (await response.json()) as {
        pseudonym?: string;
        key_version?: number;
      };
      if (!body.pseudonym) return null;
      this.pseudonym = body.pseudonym;
      this.identified = true;
      return { pseudonym: body.pseudonym, keyVersion: body.key_version ?? 0 };
    } catch {
      return null;
    }
  }

  private async maybeIdentify(): Promise<void> {
    if (this.identified) return;
    await this.identify();
  }

  private async resolveAuthHeader(): Promise<Record<string, string>> {
    const token = await getTelemetryToken(this.config);
    if (token) return { Authorization: `Bearer ${token}` };
    return { "X-Emmi-App-Key": this.config.appKey };
  }

  private async send(
    item: QueuedEvent,
    authHeader: Record<string, string>,
  ): Promise<void> {
    // Stamped fresh on every attempt — a retried event's sent_at reflects
    // when THIS attempt actually went out, not when it was first enqueued
    // (batch 4B item 4). occurred_at (set once in track()) stays untouched.
    item.envelope.sent_at = new Date().toISOString();
    try {
      const response = await fetchWithTimeout(
        `${this.config.ingestUrl}/v1/events`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader },
          body: JSON.stringify(item.envelope),
          // Best-effort survival of a page-hide-triggered flush; browsers cap
          // keepalive request bodies at 64KB, well above one 256KB-max event.
          keepalive: true,
        },
        EVENT_FETCH_TIMEOUT_MS,
      );
      if (response.ok || response.status < 500) return;
      throw new Error(`event-ingest responded ${response.status}`);
    } catch {
      item.attempts += 1;
      if (item.attempts >= MAX_ATTEMPTS) return; // dropped: never blocks the UI
      setTimeout(() => {
        void this.retrySend(item);
      }, RETRY_BASE_DELAY_MS * item.attempts);
    }
  }

  private async retrySend(item: QueuedEvent): Promise<void> {
    const authHeader = await this.resolveAuthHeader();
    await this.send(item, authHeader);
  }
}
